/** Portal › Book a Meeting: what each of my accounts includes or costs, open times, and my upcoming meetings. */
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarCheck } from "lucide-react";
import { useState } from "react";
import { phoneLabel } from "@clientflow/shared";
import { LocationNote, money, SlotPicker, TextOptIn, whenLabel } from "@/components/booking-ui";
import { ask } from "@/components/dialogs";
import { Empty, Loading, PageHeader, Section } from "@/components/page";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, errorMessage } from "@/lib/api";
import { toast } from "@/lib/use-toast";
import { cn } from "@/lib/utils";

type Offer = { id: string; name: string; description: string; durationMinutes: number; location: "video" | "phone"; rule: string; charge: "free" | "included" | "paid"; priceCents: number; label: string; remaining: number | null; canInvoice: boolean };
type Upcoming = { id: string; clientId: string; startsAt: string; endsAt: string; status: string; charge: string; priceCents: number; meetingUrl: string; typeName: string; location: "video" | "phone"; staffName: string };
type PortalBooking = { firmName: string; timeZone: string; cancelHours: number; paymentsEnabled: boolean; texts: boolean; clients: Array<{ id: string; name: string; kind: string; offers: Offer[]; smsPhone: string | null; phone: string | null }>; upcoming: Upcoming[] };

function costText(o: Offer) {
  if (o.charge === "paid") return money(o.priceCents);
  if (o.charge === "included") return o.remaining === 0 ? "Used this month" : "Included";
  return "Free";
}

export function PortalBookPage() {
  const { data, isLoading } = useQuery<PortalBooking>({ queryKey: ["/api/portal/booking"] });
  const [clientId, setClientId] = useState("");
  const [offerId, setOfferId] = useState<string | null>(null);
  const [slot, setSlot] = useState<string | null>(null);
  const [note, setNote] = useState("");
  const [pay, setPay] = useState<"now" | "invoice">("now");
  // Ticked if they said yes before; changing it here changes their choice for every meeting.
  const [textMe, setTextMe] = useState<boolean | null>(null);
  const [textPhone, setTextPhone] = useState<string | null>(null);
  const [callPhone, setCallPhone] = useState<string | null>(null);
  const paid = new URLSearchParams(window.location.search).get("paid");
  const client = data?.clients.find((c) => c.id === clientId) ?? data?.clients[0];
  const offer = client?.offers.find((o) => o.id === offerId) ?? null;
  const texting = textMe ?? !!client?.smsPhone;
  const phone = textPhone ?? (client?.smsPhone ? phoneLabel(client.smsPhone) : "");
  const callAt = callPhone ?? client?.phone ?? "";
  const book = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ status: string; checkoutUrl: string | null }>("POST", "/api/portal/bookings", { clientId: client!.id, meetingTypeId: offer!.id, startsAt: slot, pay: offer!.charge === "paid" && offer!.canInvoice ? pay : "now", note, ...(offer!.location === "phone" ? { phone: callAt } : {}), ...(data!.texts ? { textMe: texting, textPhone: phone } : {}) }),
    onSuccess: (r) => {
      if (r.checkoutUrl) {
        window.location.assign(r.checkoutUrl);
        return;
      }
      toast({ title: "Your meeting is booked", description: `${offer!.name}, ${whenLabel(slot!, data!.timeZone)}. A confirmation is on its way.` });
      setOfferId(null);
      setSlot(null);
      setNote("");
    },
  });
  const cancel = useMutation({ mutationFn: (id: string) => api.send<{ counts: boolean }>("POST", `/api/portal/bookings/${id}/cancel`) });

  if (isLoading || !data) return <Loading />;
  const mine = data.upcoming.filter((u) => !client || u.clientId === client.id);
  return (
    <>
      <PageHeader title="Book a Meeting" description="Meetings included with your services, and consultations whenever you need one." />
      {paid === "1" && <p className="mb-4 rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm dark:border-emerald-900 dark:bg-emerald-950/40">Thank you, your payment was received. Your meeting is confirmed and a confirmation email is on its way.</p>}
      {data.clients.length > 1 && (
        <div className="mb-4 flex flex-wrap gap-1.5">
          {data.clients.map((c) => (
            <button key={c.id} type="button" onClick={() => (setClientId(c.id), setOfferId(null), setSlot(null), setTextMe(null), setTextPhone(null), setCallPhone(null))} className={cn("rounded-full border px-3 py-1 text-xs font-medium", c.id === client?.id ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card")}>
              {c.name}
            </button>
          ))}
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <div className="min-w-0 space-y-6">
          <Section title="Choose a meeting">
            {!client?.offers.length ? (
              <Empty>There's nothing to book for this account right now. Send us a message and we'll find a time.</Empty>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {client.offers.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => (setOfferId(o.id), setSlot(null))}
                    className={cn("rounded-xl border bg-card p-4 text-left transition-colors", o.id === offerId ? "border-primary ring-1 ring-primary" : "border-card-border hover:border-primary/50")}
                  >
                    <span className="flex items-start justify-between gap-2">
                      <span className="font-medium">{o.name}</span>
                      <span className={cn("shrink-0 text-sm font-semibold tabular", o.charge !== "paid" && "text-emerald-700 dark:text-emerald-300")}>{costText(o)}</span>
                    </span>
                    <span className="mt-1 block text-sm text-muted-foreground">{o.description}</span>
                    {o.charge === "included" && <span className="mt-1 block text-xs font-medium text-emerald-700 dark:text-emerald-300">{o.label}</span>}
                    <span className="mt-2 block">
                      <LocationNote location={o.location} durationMinutes={o.durationMinutes} />
                    </span>
                  </button>
                ))}
              </div>
            )}
          </Section>
          {offer && client && (
            <Section title="Choose a time">
              <SlotPicker queryKey={`${client.id}:${offer.id}`} timeZone={data.timeZone} value={slot} onChange={setSlot} load={(from, to) => api.get<string[]>(`/api/portal/booking/slots?meetingTypeId=${offer.id}&clientId=${client.id}&from=${from}&to=${to}`)} />
            </Section>
          )}
          {offer && slot && (
            <Section title="Confirm">
              <form
                className="space-y-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  book.mutate();
                }}
              >
                <p className="text-sm">
                  <span className="font-medium">{offer.name}</span> · {whenLabel(slot, data.timeZone)}
                </p>
                {offer.charge === "paid" &&
                  (offer.canInvoice ? (
                    <fieldset className="space-y-1.5 text-sm">
                      <legend className="mb-1 text-xs text-muted-foreground">{money(offer.priceCents)}: how would you like to pay?</legend>
                      {data.paymentsEnabled && (
                        <label className="flex items-center gap-2">
                          <input type="radio" name="pay" checked={pay === "now"} onChange={() => setPay("now")} /> Pay now by card
                        </label>
                      )}
                      <label className="flex items-center gap-2">
                        <input type="radio" name="pay" checked={pay === "invoice" || !data.paymentsEnabled} onChange={() => setPay("invoice")} /> Add it to my next invoice
                      </label>
                    </fieldset>
                  ) : (
                    <p className="text-sm text-muted-foreground">{data.paymentsEnabled ? `${money(offer.priceCents)}, paid by card when you book.` : `${money(offer.priceCents)}; we'll send you an invoice.`}</p>
                  ))}
                {offer.location === "phone" && (
                  <div className="max-w-xs space-y-1">
                    <Label htmlFor="pb-call">Phone number we should call</Label>
                    <Input id="pb-call" type="tel" autoComplete="tel" required value={callAt} onChange={(e) => setCallPhone(e.target.value)} />
                  </div>
                )}
                <div className="space-y-1">
                  <Label htmlFor="pb-note">Anything we should prepare? (optional)</Label>
                  <Textarea id="pb-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)} />
                </div>
                {data.texts && (
                  <div className="space-y-2">
                    <TextOptIn id="pb-texts" firm={data.firmName} checked={texting} onChange={setTextMe} />
                    {texting && (
                      <div className="max-w-xs space-y-1">
                        <Label htmlFor="pb-phone">Mobile phone</Label>
                        <Input id="pb-phone" type="tel" autoComplete="tel" required value={phone} onChange={(e) => setTextPhone(e.target.value)} />
                      </div>
                    )}
                  </div>
                )}
                {book.error && <p className="text-sm text-destructive">{errorMessage(book.error)}</p>}
                <Button type="submit" disabled={book.isPending}>
                  <CalendarCheck className="h-4 w-4" /> {offer.charge === "paid" && (pay === "now" || !offer.canInvoice) && data.paymentsEnabled ? "Continue to payment" : "Book this time"}
                </Button>
              </form>
            </Section>
          )}
        </div>
        <Section title="Your upcoming meetings" description={`Cancel at least ${data.cancelHours} hours ahead and there's no charge.`}>
          {!mine.length ? (
            <p className="text-sm text-muted-foreground">No meetings booked.</p>
          ) : (
            <ul className="divide-y divide-border">
              {mine.map((u) => (
                <li key={u.id} className="py-3 text-sm">
                  <p className="font-medium">{u.typeName}</p>
                  <p className="text-muted-foreground">{whenLabel(u.startsAt, data.timeZone)}</p>
                  <p className="text-xs text-muted-foreground">
                    With {u.staffName}
                    {u.status === "pending_payment" && " · waiting for payment"}
                  </p>
                  <div className="mt-1.5 flex flex-wrap gap-3">
                    {u.meetingUrl && (
                      <a className="text-xs font-medium text-primary hover:underline" href={u.meetingUrl} target="_blank" rel="noreferrer">
                        Join video
                      </a>
                    )}
                    <button
                      type="button"
                      className="text-xs text-destructive hover:underline"
                      onClick={() =>
                        void ask({
                          title: "Cancel this meeting?",
                          description: Date.parse(u.startsAt) - Date.now() < data.cancelHours * 3_600_000 ? `It's less than ${data.cancelHours} hours away, so it still counts as used${u.priceCents ? " and its charge stays" : ""}.` : "There's no charge for cancelling now.",
                          confirmLabel: "Cancel meeting",
                          destructive: true,
                        }).then((ok) => ok && cancel.mutate(u.id))
                      }
                    >
                      Cancel
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {cancel.error && <p className="mt-2 text-sm text-destructive">{errorMessage(cancel.error)}</p>}
        </Section>
      </div>
    </>
  );
}
